import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { safeDateMs } from "../../../sortLogic";
import { isVideoFilename, videoMime } from "../../../utils";
import { mediaUrl } from "../../../mediaAuth";
import { useVideoPosterFrame } from "../../hooks/useVideoPosterFrame";
import type { MediaItem } from "../../../types";

// Exported so the Home dashboard's "Memory of the day" featured card
// (below) can pick today's best match without duplicating this
// date-matching logic - same underlying On This Day data either way.
export function memoriesForToday(media: MediaItem[]): Array<{ m: MediaItem; index: number; yearsAgo: number }> {
  const now = new Date();
  const thisYear = now.getFullYear();

  return media
    .map((m, index) => ({ m, index, ms: safeDateMs(m) }))
    .filter(({ ms }) => ms > 0)
    .filter(({ ms }) => {
      const d = new Date(ms);
      return d.getMonth() === now.getMonth() && d.getDate() === now.getDate() && d.getFullYear() !== thisYear;
    })
    .map(({ m, index, ms }) => ({ m, index, yearsAgo: thisYear - new Date(ms).getFullYear() }))
    .sort((a, b) => a.yearsAgo - b.yearsAgo);
}

// Poster-frame-seek for a silent video preview - shared with
// GalleryCard.tsx/MediaPreviewCard.tsx via useVideoPosterFrame (video
// playback audit, Low #11 - previously a third independent copy of the
// same effect).
function FeaturedMemoryMedia({ item }: { item: MediaItem }) {
  const isVid = isVideoFilename(item.filename);
  const videoRef = useVideoPosterFrame(item.filename, isVid, "memories-row-featured");

  if (isVid) {
    return (
      <>
        <video ref={videoRef} className="featured-memory-media" preload="none" muted playsInline>
          <source src={mediaUrl(`/assets/media/${item.filename}`)} type={videoMime(item.filename)} />
        </video>
        <span className="featured-memory-play" aria-hidden="true">
          <i className="bi bi-play-circle-fill" />
        </span>
      </>
    );
  }
  return <img className="featured-memory-media" src={mediaUrl(`/assets/thumbs/${item.filename}`)} alt="" loading="lazy" />;
}

// Portals into #memoriesSection, owning both its visibility conditions
// entirely reactively (Home-only nav condition AND "are there memories
// today" content condition). Home-only is a route check (useLocation)
// rather than appState.activeTab - the URL is the single source of truth
// for "which top-level screen".
//
// Presented as one large "Memory of the day" featured card (the best/most
// recent-anniversary match) rather than a horizontal row of small
// thumbnails - closer to a premium couples-app feel and lets media itself
// be the visual surface instead of another boxed panel. Still the exact
// same underlying data/feature (memoriesForToday above), just a different
// presentation; a "+N more today" hint covers the rare case of more than
// one match without needing a second UI to browse them.
export function MemoriesRow() {
  const { pathname } = useLocation();
  const media = useAppState((s) => s.media);

  const container = getVanillaEl<HTMLDivElement>("memoriesSection");
  const shouldShow = pathname === "/";
  const memories = useMemo(() => (shouldShow ? memoriesForToday(media) : []), [shouldShow, media]);

  useEffect(() => {
    container?.classList.toggle("d-none", !(shouldShow && memories.length));
  }, [container, shouldShow, memories.length]);

  function onView(index: number) {
    appState.currentMediaIndex = index;
    notifyStateChange();
  }

  if (!container || !memories.length) return null;

  const featured = memories[0];
  const extraCount = memories.length - 1;

  return createPortal(
    <>
      <div className="home-section-heading">
        <h2 className="home-section-title">Memory of the Day</h2>
        {extraCount > 0 && (
          <span className="home-section-hint">
            +{extraCount} more today
          </span>
        )}
      </div>
      <button
        type="button"
        className="featured-memory"
        onClick={() => onView(featured.index)}
        aria-label={`View memory from ${featured.yearsAgo} year${featured.yearsAgo === 1 ? "" : "s"} ago`}
      >
        <FeaturedMemoryMedia item={featured.m} />
        <span className="featured-memory-label">
          {featured.yearsAgo} year{featured.yearsAgo === 1 ? "" : "s"} ago
        </span>
      </button>
    </>,
    container,
  );
}
